// src/Pages/TeamDetails.jsx
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Container, Paper, Box, Typography, Grid, Button, CircularProgress, Snackbar, Alert, TextField, Stack, IconButton, Tooltip, Avatar } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from '../context/AuthContext';

// Entry point
export default function TeamDetails({ connectionString }) {
  const { state } = useLocation();
  const navigate = useNavigate();

  // Pull both leaderId and full leader object from location state
  const { selectedCompany } = useAuth();
  const leaderId = state?.leaderId;
  const leader = state?.leader || {};
  const leaderName = leader.EmployeeName || "—";
  const companyId = selectedCompany?.CompId || null;
  const API = connectionString;

  // State
  const [allUsers, setAllUsers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Search & pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const limit = 5;
  const [total, setTotal] = useState(0);

  // Fetch available users
  const fetchUsers = () => {
    setLoadingAll(true);
    axios.get(`${API}/api/allusers`, { params: { search: searchTerm, limit, offset: page * limit, companyId }, })
      .then((r) => {
        if (r.data.success) {
          setAllUsers(r.data.data);
          setTotal(r.data.total || 0);
          setError("");
        } else {
          setAllUsers([]);
          setError("No users found");
        }
      })
      .catch(() => setError("Server error"))
      .finally(() => setLoadingAll(false));
  };

  // Fetch team members
  const fetchTeam = () => {
    if (!leaderId) return;
    setLoadingTeam(true);
    axios.get(`${API}/api/teamdetails`, { params: { teamLeaderId: leaderId, companyId } })
      .then((r) => {
        if (r.data.success) {
          setTeamMembers(r.data.data);
          setError("");
        } else {
          setTeamMembers([]);
          setError("No members");
        }
      })
      .catch(() => setError("Server error"))
      .finally(() => setLoadingTeam(false));
  };

  useEffect(fetchUsers, [searchTerm, page]);
  useEffect(fetchTeam, [leaderId]);

  // Add a user to the team
  const handleAddUser = (userIdToAdd) => {
    const userToAdd = allUsers.find((u) => u.UserId === userIdToAdd);
    if (!userToAdd) {
      setError("Selected user not found.");
      return;
    }

    const payload = {
      teamLeaderId: leaderId,
      userIds: [userIdToAdd],
      companyId,
      Eusername: userToAdd.UserName,
      EuserId: userToAdd.UserId,
      UnderUser: { ...leader, userName: leader.UserName }, // Send the leader's username as a string
      UnderUserId: leaderId,
    };

    axios.post(`${API}/api/teamdetails/add`, payload)
      .then((response) => {
        if (response.data.success) {
          setTeamMembers((prev) => [...prev, userToAdd]);
          setAllUsers((prev) => prev.filter((u) => u.UserId !== userIdToAdd));
          setSuccess("Team member added successfully.");
        } else {
          setError(response.data.message || "Server error while addingEquipe member.");
        }
      })
      .catch(() => setError("Server error while adding team member."));
  };

  // Remove a user from the team
  const handleRemoveUser = (userIdToRemove) => {
    axios.delete(`${API}/api/teamdetails/remove`, { data: { teamLeaderId: leaderId, userIds: [userIdToRemove], companyId }, })
      .then((res) => {
        if (res.data.success) {
          const userToRemove = teamMembers.find((u) => u.UserId === userIdToRemove);
          if (userToRemove) {
            setTeamMembers((prev) => prev.filter((u) => u.UserId !== userIdToRemove));
            setAllUsers((prev) => [...prev, userToRemove]);
          }
          setSuccess("Team member removed successfully.");
        }
      })
      .catch(() => setError("Server error while removing team member."));
  };

  const totalPages = Math.ceil(total / limit);

  // Contact card component
  const ContactCard = ({ user, onAction, actionIcon, actionColor, actionTitle }) => (
    <Paper elevation={2} sx={{ p: 2, mb: 2, display: "flex", alignItems: "center", borderRadius: 2 }} >
      <Avatar sx={{ bgcolor: actionColor, mr: 2 }}> {user.UserName?.[0]?.toUpperCase()} </Avatar>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography noWrap fontWeight={600}> {user.EmployeeName} </Typography>
        <Typography noWrap variant="caption" color="text.secondary"> @{user.UserName} </Typography>
      </Box>
      <Tooltip title={actionTitle}>
        <IconButton color={actionColor} onClick={() => onAction(user.UserId)}> {actionIcon} </IconButton>
      </Tooltip>
    </Paper>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Team for: {leaderName}</Typography>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}> Back </Button>
          <Button startIcon={<CloseIcon />} onClick={() => navigate("/main/home")}> Close </Button>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}> {error} </Alert>
      )}

      <Grid container spacing={4}>
        {/* Available Users */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}> Available Users </Typography>
            <TextField sx={{ mb: 2 }} placeholder="Search..." size="small" fullWidth value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
            />
            {loadingAll ? (
              <Box textAlign="center" py={4}> <CircularProgress /> </Box>
            ) : allUsers.length ? (
              allUsers.map((u) => (
                <ContactCard key={u.UserId} user={u} onAction={handleAddUser} actionIcon={<AddIcon />} actionColor="primary" actionTitle="Add to Team" />
              ))
            ) : (
              <Typography color="text.secondary" align="center"> No available users </Typography>
            )}
            {totalPages > 1 && (
              <Box textAlign="center" sx={{ mt: 2 }}>
                {[...Array(totalPages)].map((_, idx) => (
                  <Button key={idx} size="small" variant={idx === page ? "contained" : "outlined"} onClick={() => setPage(idx)} sx={{ mx: 0.5 }} > {idx + 1} </Button>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Team Members */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}> Team Members </Typography>
            {loadingTeam ? (
              <Box textAlign="center" py={4}> <CircularProgress /> </Box>
            ) : teamMembers.length ? (
              teamMembers.map((u) => (
                <ContactCard key={u.UserId} user={u} onAction={handleRemoveUser} actionIcon={<RemoveIcon />} actionColor="error" actionTitle="Remove from Team" />
              ))
            ) : (<Typography color="text.secondary" align="center"> No team members </Typography>)}
          </Paper>
        </Grid>
      </Grid>

      {/* Success Snackbar */}
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess("")} message={success} />
    </Container>
  );
}